import React from 'react';
import { useNavigate } from 'react-router-dom';

const styles = {
  page: { maxWidth: 860, margin: '0 auto', padding: '0 1.5rem 3rem', fontFamily: 'Georgia, serif' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '0.5px solid rgba(0,0,0,0.12)', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' },
  logo: { fontSize: 22, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.3px', fontFamily: 'Georgia, serif' },
  logoSub: { fontSize: 11, color: '#888', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'monospace', marginTop: 2 },
  nav: { display: 'flex', gap: '1.25rem', flexWrap: 'wrap' },
  navLink: { fontSize: 13, color: '#666', textDecoration: 'none', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'Georgia, serif' },
  breadcrumb: { fontSize: 12, color: '#999', fontFamily: 'monospace', marginBottom: '1.25rem', cursor: 'pointer' },
  hero: { marginBottom: '2rem' },
  heroTitle: { fontSize: 32, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', marginBottom: '0.5rem', lineHeight: 1.2 },
  heroSub: { fontSize: 14, color: '#666', lineHeight: 1.7, maxWidth: 620 },
  sectionLabel: { fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'monospace', marginBottom: '0.75rem', marginTop: '2rem' },
  card: { border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: 12, background: '#fff', padding: '1.5rem', marginBottom: '1rem' },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', marginBottom: '0.5rem' },
  cardText: { fontSize: 14, color: '#555', lineHeight: 1.75 },
  catBlock: { border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: 12, background: '#fff', marginBottom: '0.75rem', overflow: 'hidden' },
  catHeader: { padding: '0.9rem 1.25rem', borderBottom: '0.5px solid rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' },
  catNum: { fontSize: 11, fontFamily: 'monospace', color: '#999', width: 20, flexShrink: 0 },
  catTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif' },
  catRef: { fontSize: 11, color: '#999', fontFamily: 'monospace', marginTop: 2 },
  catWeight: { fontSize: 12, padding: '3px 10px', borderRadius: 99, flexShrink: 0 },
  catBody: { padding: '1rem 1.25rem', fontSize: 14, color: '#555', lineHeight: 1.7 },
  verseBlock: { borderLeft: '2px solid #F09595', padding: '0.65rem 0.9rem', margin: '0.75rem 0', background: '#FCEBEB', borderRadius: '0 8px 8px 0' },
  verseText: { fontSize: 13, color: '#791F1F', fontFamily: 'Georgia, serif', lineHeight: 1.6, fontStyle: 'italic' },
  verseRef: { fontSize: 11, color: '#A32D2D', fontFamily: 'monospace', marginTop: 3 },
  scoreTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: '0.5rem' },
  scoreTh: { textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: 11, color: '#999', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '0.5px solid rgba(0,0,0,0.1)' },
  scoreTd: { padding: '0.5rem 0.75rem', borderBottom: '0.5px solid rgba(0,0,0,0.05)', color: '#555', verticalAlign: 'top' },
  faqBlock: { border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: 12, background: '#fff', marginBottom: '0.75rem', overflow: 'hidden' },
  faqQ: { padding: '1rem 1.25rem', fontSize: 15, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif', borderBottom: '0.5px solid rgba(0,0,0,0.08)' },
  faqA: { padding: '1rem 1.25rem', fontSize: 14, color: '#555', lineHeight: 1.75 },
  disclaimer: { fontSize: 11, color: '#999', lineHeight: 1.65, borderTop: '0.5px solid rgba(0,0,0,0.08)', paddingTop: '1rem', fontStyle: 'italic', marginTop: '2rem' },
  hornNote: { border: '0.5px solid #F09595', borderRadius: 12, background: '#FCEBEB', padding: '1.25rem', marginBottom: '1rem' },
  hornTitle: { fontSize: 15, fontWeight: 700, color: '#791F1F', fontFamily: 'Georgia, serif', marginBottom: '0.5rem' },
  hornText: { fontSize: 14, color: '#791F1F', lineHeight: 1.7 },
};

const categories = [
  {
    num: 'I',
    title: 'Claims of supreme authority & self-deification',
    ref: 'Daniel 11:36–37 · 2 Thessalonians 2:3–4 · Revelation 13:5–6',
    weight: 20,
    pillBg: '#FCEBEB',
    pillColor: '#A32D2D',
    desc: 'The Antichrist is distinguished above all by an extraordinary compulsion toward self-magnification that transcends ordinary political ambition. This category assesses whether the candidate positions themselves as uniquely chosen, messianic, or cosmically significant — particularly when this framing bypasses or subordinates existing religious authority.',
    verse: '"He shall exalt himself and magnify himself above every god, and shall speak astonishing things against the God of gods. He shall prosper till the indignation is accomplished."',
    verseRef: 'Daniel 11:36',
  },
  {
    num: 'II',
    title: 'Global dominion ambitions & geopolitical control',
    ref: 'Revelation 13:7 · Daniel 7:23 · Daniel 11:39–43',
    weight: 20,
    pillBg: '#FAEEDA',
    pillColor: '#854F0B',
    desc: 'This category examines the scope of territorial, political, and institutional ambition. The prophetic figure is not merely a national leader but one who actively seeks authority over multiple nations, redistributes power to loyalists, and systematically undermines existing international structures for personal strategic advantage.',
    verse: '"Authority was given it over every tribe and people and language and nation."',
    verseRef: 'Revelation 13:7',
  },
  {
    num: 'III',
    title: 'Economic control & the Mark of the Beast',
    ref: 'Revelation 13:16–17 · Revelation 18:11–13',
    weight: 15,
    pillBg: '#EEEDFE',
    pillColor: '#3C3489',
    desc: 'Perhaps the most technically specific prophecy in all of eschatological literature: a system in which economic participation — buying and selling — is contingent on allegiance to the figure in question. This category assesses whether the candidate leverages financial systems, trade access, or commercial infrastructure as instruments of loyalty enforcement.',
    verse: '"Also it causes all, both small and great, both rich and poor, both free and slave, to be marked on the right hand or the forehead, so that no one can buy or sell unless he has the mark."',
    verseRef: 'Revelation 13:16–17',
  },
  {
    num: 'IV',
    title: 'Persecution of dissent & opposition',
    ref: 'Revelation 13:7 · Revelation 12:17 · Daniel 7:21 · Daniel 7:25',
    weight: 15,
    pillBg: '#FAECE7',
    pillColor: '#993C1D',
    desc: 'The prophetic figure is characterised by active, systematic hostility toward those who refuse personal allegiance. This goes beyond ordinary political opposition — it involves the use of legal, institutional, and extrajudicial mechanisms to punish, marginalise, or eliminate critics, dissenters, and moral objectors.',
    verse: '"He shall think to change the times and the law, and they shall be given into his hand for a time, times, and half a time."',
    verseRef: 'Daniel 7:25',
  },
  {
    num: 'V',
    title: 'Deceptive peace & false covenant diplomacy',
    ref: 'Daniel 8:25 · Daniel 9:27 · 1 Thessalonians 5:3 · Revelation 6:2',
    weight: 10,
    pillBg: '#E1F5EE',
    pillColor: '#0F6E56',
    desc: 'The Antichrist is a self-styled peacemaker whose agreements sow destruction. This category assesses the pattern of grand diplomatic gestures — deals, treaties, covenants — that are subsequently broken, weaponised, or used to establish dominance rather than genuine peace.',
    verse: '"While people are saying Peace and safety, then sudden destruction comes upon them as labour pains come upon a pregnant woman, and they will not escape."',
    verseRef: '1 Thessalonians 5:3',
  },
  {
    num: 'VI',
    title: 'Signs, wonders & propaganda machinery',
    ref: 'Revelation 13:13–14 · 2 Thessalonians 2:9–10 · Matthew 24:24',
    weight: 10,
    pillBg: '#E6F1FB',
    pillColor: '#185FA5',
    desc: 'The deployment of spectacular spectacle, disinformation, and media dominance to manufacture loyalty and manufacture consent. This category also assesses the False Prophet dynamic — the presence of a loyal enabler class that amplifies the figure\'s power and demands allegiance on their behalf.',
    verse: '"The coming of the lawless one is by the activity of Satan with all power and false signs and wonders, and with all wicked deception for those who are perishing."',
    verseRef: '2 Thessalonians 2:9–10',
  },
  {
    num: 'VII',
    title: 'Miraculous survival & cult of death-defiance',
    ref: 'Revelation 13:3 · Revelation 13:12 · Revelation 17:8',
    weight: 10,
    pillBg: '#F1EFE8',
    pillColor: '#5F5E5A',
    desc: 'One of the most visually arresting prophetic details: the Beast appears to suffer a mortal wound, yet survives — and the world marvels. This category assesses whether the candidate has survived what appeared to be fatal or career-ending blows, and whether their survival has been mythologised into evidence of indestructibility or divine protection.',
    verse: '"One of its heads seemed to have a mortal wound, but its mortal wound was healed, and the whole earth marvelled as they followed the beast."',
    verseRef: 'Revelation 13:3',
  },
];

const faqs = [
  {
    q: 'Is this site serious?',
    a: 'The Antichrist-o-meter is a satirical publication. The scoring framework, however, is derived from genuine biblical scholarship — the criteria are real prophetic attributes drawn from canonical texts. We apply them to public figures with rigorous mock-seriousness and zero actual theological claims. Think of it as peer-reviewed eschatological comedy.',
  },
  {
    q: 'How are scores updated?',
    a: 'Our editorial team reviews documented public behaviour daily against the seven scoring categories. Each criterion is scored 0–1 (0 = no evidence, 0.5 = partial or circumstantial, 1 = documented and unambiguous). Category scores are weighted and summed to produce the final percentage. All scoring events are logged in each candidate\'s profile with source references.',
  },
  {
    q: 'Why is the maximum score expressed as 666%?',
    a: 'The Number of the Beast (Revelation 13:18) is the most culturally recognised symbol in eschatological literature. Expressing the maximum score as 666% rather than 100% serves both comic and thematic purposes: it reminds the reader that even a perfect score is merely a data point, not a theological verdict. Also, it looks extremely good on a dashboard.',
  },
  {
    q: 'Why are historical figures included?',
    a: 'Historical candidates serve as calibration benchmarks. Hitler\'s score of 83% and Nero\'s 61% provide meaningful context for evaluating contemporary figures. They also serve the important satirical function of demonstrating that the bar is genuinely high — and that some current candidates are clearing it with disturbing ease.',
  },
  {
    q: 'Can I suggest a candidate?',
    a: 'User-submitted candidates are planned for a future release. To qualify, a figure must have sufficient documented public behaviour to score meaningfully across at least four of the seven categories. Private individuals, fictional characters, and your ex-partner do not qualify, however understandable the impulse.',
  },
  {
    q: 'Is this defamatory?',
    a: 'All scores are based on documented public behaviour and are clearly framed as satirical analysis. The site makes no claims of literal supernatural identity. Satire of public figures on matters of public concern has a long and legally robust history. We note, with some theological interest, that anyone who finds this site defamatory is probably scoring quite well on Category I.',
  },
];

export default function Methodology() {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#fafaf8', minHeight: '100vh' }}>
      <div style={styles.page}>

        <div style={styles.topbar}>
          <div>
            <div style={styles.logo}>The Antichrist-o-meter</div>
            <div style={styles.logoSub}>Daily prophetic behavioural analysis · est. 2026</div>
          </div>
          <nav style={styles.nav}>
            <button style={styles.navLink} onClick={() => navigate('/')}>Rankings</button>
            <button style={styles.navLink} onClick={() => navigate('/methodology')}>Methodology</button>
          </nav>
        </div>

        <div style={styles.breadcrumb} onClick={() => navigate('/')}>
          ← Rankings › Methodology & Scripture Index
        </div>

        <div style={styles.hero}>
          <div style={styles.heroTitle}>Methodology & Scripture Index</div>
          <div style={styles.heroSub}>A rigorous, peer-reviewed, and entirely satirical framework for the daily behavioural assessment of public figures against the prophetic criteria of Revelation, Daniel, and the Pauline Epistles. All citations are canonical. All scores are documented. All disclaimers are meant sincerely. Mostly.</div>
        </div>

        <div style={styles.sectionLabel}>Scoring framework overview</div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>The seven-category model</div>
          <div style={styles.cardText}>
            The Antichrist-o-meter scoring system is derived from a synthesis of prophetic literature across three primary sources: the Book of Revelation (particularly chapters 13, 17, and 18), the Book of Daniel (chapters 7, 8, 9, and 11), and the Pauline Epistles (principally 2 Thessalonians 2 and 1 John 2–4).
            <br /><br />
            Seven behavioural categories are identified, each weighted according to its prophetic prominence and specificity. Each category contains three criteria, scored on a 0–1 scale. The maximum raw score is 100 points, which is expressed as 666% — a deliberate and theologically calibrated editorial choice.
            <br /><br />
            Scores are updated daily based on documented public behaviour. Historical figures receive fixed scores based on the totality of their documented record. All scoring decisions are logged with source references in individual candidate profiles.
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Scoring scale</div>
          <table style={styles.scoreTable}>
            <thead>
              <tr>
                <th style={styles.scoreTh}>Value</th>
                <th style={styles.scoreTh}>Meaning</th>
                <th style={styles.scoreTh}>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.scoreTd}><strong>0.0</strong></td>
                <td style={styles.scoreTd}>No evidence</td>
                <td style={styles.scoreTd}>No documented behaviour matching this criterion</td>
              </tr>
              <tr>
                <td style={styles.scoreTd}><strong>0.5</strong></td>
                <td style={styles.scoreTd}>Partial / circumstantial</td>
                <td style={styles.scoreTd}>Behaviour present but ambiguous, isolated, or unverified</td>
              </tr>
              <tr>
                <td style={styles.scoreTd}><strong>1.0</strong></td>
                <td style={styles.scoreTd}>Documented & unambiguous</td>
                <td style={styles.scoreTd}>Clear, repeated, verified pattern of behaviour matching criterion</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={styles.hornNote}>
          <div style={styles.hornTitle}>On the Horn Chart</div>
          <div style={styles.hornText}>
            The daily score history for each candidate is rendered as what we call the Horn Chart — a reference to Daniel 7:8, in which the Antichrist emerges as "a little horn" that grows to dominate all others. A candidate whose score curve rises sharply and continuously while rivals plateau is not merely scoring well. They are, in the strictly data-visualisation sense, fulfilling the geometry of the prophecy. We consider this an elegant feature of the design.
            <br /><br />
            <em>"There came up among them another horn, a little one, before which three of the first horns were plucked up by the roots."</em> — Daniel 7:8
          </div>
        </div>

        <div style={styles.sectionLabel}>The seven categories · full scripture index</div>

        {categories.map((cat, i) => (
          <div key={i} style={styles.catBlock}>
            <div style={styles.catHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={styles.catNum}>{cat.num}</span>
                <div>
                  <div style={styles.catTitle}>{cat.title}</div>
                  <div style={styles.catRef}>{cat.ref}</div>
                </div>
              </div>
              <span style={{ ...styles.catWeight, background: cat.pillBg, color: cat.pillColor }}>{cat.weight} pts</span>
            </div>
            <div style={styles.catBody}>
              {cat.desc}
              <div style={styles.verseBlock}>
                <div style={styles.verseText}>{cat.verse}</div>
                <div style={styles.verseRef}>{cat.verseRef}</div>
              </div>
            </div>
          </div>
        ))}

        <div style={styles.sectionLabel}>Frequently asked questions</div>

        {faqs.map((faq, i) => (
          <div key={i} style={styles.faqBlock}>
            <div style={styles.faqQ}>{faq.q}</div>
            <div style={styles.faqA}>{faq.a}</div>
          </div>
        ))}

        <div style={styles.disclaimer}>
          The Antichrist-o-meter is a satirical publication for entertainment purposes. No theological claims are made or implied. No supernatural identities are asserted. All biblical citations are from the English Standard Version (ESV) unless otherwise noted. The editors are not theologians, eschatologists, or prophets. We are, however, paying close attention.
        </div>

      </div>
    </div>
  );
}
